import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { decidePostLoginPath } from "../../utils/postLoginRedirect";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const inputClass =
  "w-full p-3 rounded-xl bg-black/40 border border-white/20 text-white placeholder-white/30 " +
  "outline-none focus:border-[#00FF99]/50 transition disabled:opacity-60 disabled:cursor-not-allowed";

const readOnlyInputClass =
  "w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white/80 outline-none cursor-not-allowed";

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs text-[#00FF99]/70 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

// Hoisted out of AcceptInvite() so its identity is stable across re-renders.
// Defining it inside the parent caused the entire subtree (including inputs)
// to unmount/remount on every keystroke, killing focus.
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-lg border border-white/10">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-[#00FF99]">KweryAI</h1>
          <p className="text-gray-400 text-sm mt-1">Accept your invitation</p>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AcceptInvite() {
  const { login } = useContext(AuthContext);

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get("token") || "").trim();
  }, []);

  const [verifyState, setVerifyState] = useState("loading"); // loading | ok | error
  const [verifyError, setVerifyError] = useState("");
  const [invite, setInvite] = useState(null);

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!token) {
      setVerifyState("error");
      setVerifyError("This invite link is missing its token. Ask your administrator for a new link.");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/onboarding/invite/verify?token=${encodeURIComponent(token)}`
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 410) {
            setVerifyError(data.detail || "This invite has expired or already been used.");
          } else if (res.status === 404) {
            setVerifyError(data.detail || "This invite link is invalid.");
          } else {
            setVerifyError(data.detail || "We couldn't verify this invite. Please try again.");
          }
          setVerifyState("error");
          return;
        }
        setInvite(data);
        setFullName(data.full_name || "");
        setVerifyState("ok");
      } catch {
        if (cancelled) return;
        setVerifyError("Network error — please check your connection and try again.");
        setVerifyState("error");
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const validate = () => {
    if (!fullName.trim()) return "Please enter your full name.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (password !== confirm) return "Passwords do not match.";
    return "";
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    const err = validate();
    if (err) { setSubmitError(err); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/onboarding/invite/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          full_name: fullName.trim(),
          password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data.detail || "Could not set up your account. Please try again.");
        setSubmitting(false);
        return;
      }

      if (data.token && data.user) {
        login(data.token, data.user, data.refresh_token);
      }
      // Brand-new account — guarantee the welcome screen shows on first entry.
      try {
        sessionStorage.removeItem("kira_welcomed");
        sessionStorage.removeItem("kira_from_login");
      } catch {}
      const dest = await decidePostLoginPath({
        user: data.user,
        token: data.token,
        // Accepting an invite is always a "first login" — they've just
        // set their password for the very first time.
        is_first_login: data.is_first_login ?? true,
      });
      // eslint-disable-next-line no-console
      console.log("[login] navigating to:", dest);
      window.location.href = dest;
    } catch {
      setSubmitError("Network error — please try again.");
      setSubmitting(false);
    }
  };

  if (verifyState === "loading") {
    return (
      <Shell>
        <p className="text-center text-white/70">Verifying your invite…</p>
      </Shell>
    );
  }

  if (verifyState === "error") {
    return (
      <Shell>
        <h2 className="text-xl font-semibold text-white mb-3 text-center">
          Invite unavailable
        </h2>
        <p className="text-red-400 text-sm text-center mb-2">{verifyError}</p>
        <p className="text-white/50 text-xs text-center mb-6">
          Contact your administrator to issue a new invite link.
        </p>
        <Link
          to="/login"
          className="block w-full text-center bg-[#00FF99] text-black font-semibold p-3 rounded-xl hover:bg-[#00cc7a] transition"
        >
          Back to Sign In
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <h2 className="text-xl font-semibold text-white mb-1 text-center">
        Welcome to {invite?.org_name || "your organisation"}
      </h2>
      <p className="text-white/50 text-sm text-center mb-6">
        Set up your account to get started.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <Field label="Organisation">
          <input
            type="text"
            value={invite?.org_name || ""}
            readOnly
            className={readOnlyInputClass}
          />
        </Field>

        <Field label="Email">
          <input
            type="email"
            value={invite?.email || ""}
            readOnly
            className={readOnlyInputClass}
          />
        </Field>

        <Field label="Full name">
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your full name"
            className={inputClass}
            disabled={submitting}
            autoFocus={!fullName}
          />
        </Field>

        <Field label="Password">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className={inputClass + " pr-11"}
              disabled={submitting}
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-white/50 hover:text-white"
              tabIndex={-1}
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Field>

        <Field label="Confirm password">
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className={inputClass + " pr-11"}
              disabled={submitting}
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-white/50 hover:text-white"
              tabIndex={-1}
              aria-label={showConfirm ? "Hide password" : "Show password"}
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Field>

        {submitError && (
          <p className="text-red-400 text-sm mb-3" role="alert">{submitError}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#00FF99] text-black font-semibold p-3 rounded-xl hover:bg-[#00cc7a] transition disabled:opacity-50"
        >
          {submitting ? "Setting up your account…" : "Set up my account"}
        </button>

        <p className="text-white/40 text-xs text-center mt-4">
          By continuing you agree to your organisation's workspace terms.
        </p>
      </form>
    </Shell>
  );
}
